import Button from "kustom-front/src/components/atoms/Button";
import noop from "lodash/noop";
import { FunctionComponent, useCallback, useState } from "react";
import { useIntl } from "react-intl";
import CartButtonSpinner from "@Components/atoms/cart/CartButtonSpinner";
import Checkbox from "@Components/atoms/Checkbox";
import Input from "@Components/atoms/Input";
import useNotificationsContext from "@Hooks/useNotificationsContext";
import { ScreenParams } from "@Types/login";
import { getConfidentialityLink } from "@Utils/links";
import { createAccount, logUserWithPassword, recoverPassword } from "./utils";
import "./style.scss";

interface Props {
  onChangeScreen: (newScreenParams: ScreenParams) => void;
  params: ScreenParams;
}

const Password: FunctionComponent<Props> = ({ onChangeScreen, params }) => {
  const [checked, setChecked] = useState<boolean>(false);
  const [disabled, setDisabled] = useState<boolean>(
    params.disabledAction ? params.disabledAction : false
  );
  const [firstName, setFirstName] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [submittedForm, setSubmittedForm] = useState<boolean>(false);

  const intl = useIntl();
  const { dispatchNotification } = useNotificationsContext();

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement> | React.MouseEvent<Element, MouseEvent>) => {
      event.preventDefault();
      setDisabled(true);
      setSubmittedForm(true);

      if (params.email && params.action === "signIn") {
        logUserWithPassword(
          { email: params.email, firstName, password },
          onChangeScreen,
          dispatchNotification,
          setDisabled,
          intl.locale
        );
      } else if (params.email && params.action === "signUp") {
        createAccount(
          { checked, email: params.email, firstName, password },
          onChangeScreen,
          dispatchNotification,
          setDisabled,
          intl.locale
        );
      }
    },
    [checked, dispatchNotification, firstName, onChangeScreen, params, password]
  );

  return (
    <div className="Password">
      <div className="Password-title">{params.email}</div>
      <div className="Password-description">
        {intl.formatMessage({ id: `cheerz_LoginModal_${params.action}_password_description` })}
      </div>
      <form onSubmit={handleSubmit}>
        {params.action === "signUp" && (
          <Input
            autocomplete="off"
            autofocus={params.action === "signUp" ? true : false}
            name="firstName"
            onChange={(newFirstName: string) => setFirstName(newFirstName)}
            placeholder={intl.formatMessage({ id: "cheerz_LoginModal_password_firstName_input" })}
            required={true}
            submitted={submittedForm}
            type="text"
            value={firstName}
          />
        )}
        <Input
          autocomplete="off"
          autofocus={params.action === "signIn" ? true : false}
          name="password"
          onChange={(newPassword: string) => setPassword(newPassword)}
          placeholder={intl.formatMessage({ id: "cheerz_LoginModal_password_input" })}
          required={true}
          submitted={submittedForm}
          type="password"
          value={password}
        />
        {params.action === "signIn" && (
          <div
            className="Password-forgotten"
            onClick={() =>
              recoverPassword(params.email, onChangeScreen, dispatchNotification, intl.locale)
            }
          >
            {intl.formatMessage({ id: "cheerz_LoginModal_password_forgotten" })}
          </div>
        )}
        {params.action === "signUp" && (
          <>
            <div className="Password-warning">
              {intl.formatMessage({ id: "cheerz_LoginModal_password_warning" })}
            </div>
            <div className="Password-newsletter" onClick={() => setChecked(!checked)}>
              <div className="Password-newsletter-checkbox">
                <Checkbox checked={checked} onChange={noop} type="square" />
              </div>
              <div className="Password-newsletter-infos">
                <div className="Password-newsletter-infos-description">
                  {intl.formatMessage({ id: "cheerz_LoginModal_password_newsletter" })}
                </div>
              </div>
            </div>
          </>
        )}
        <Button disabled={disabled} type="primary" onClick={handleSubmit}>
          {disabled ? (
            <CartButtonSpinner />
          ) : (
            intl.formatMessage({ id: "cheerz_LoginModal_password_button" })
          )}
        </Button>
      </form>
      {params.action === "signUp" && (
        <div className="Password-notice">
          {intl.formatMessage({ id: "cheerz_LoginModal_password_notice" })}
          <a href={getConfidentialityLink(intl.locale)} rel="noopener noreferrer" target="_blank">
            {intl.formatMessage({ id: "cheerz_LoginModal_password_notice_link" })}
          </a>
          .
        </div>
      )}
    </div>
  );
};

export default Password;
